import React, { useState, useEffect } from "react";
import { Button, message, Space } from "antd";
import { WindowsOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import locale from "@aligov/global-locale";
import PasswordLoginForm from "./components/PasswordLoginForm";
import "./index.css";

const LoginPage = () => {
  const [loginMode, setLoginMode] = useState("sso"); // 'sso' | 'password'
  const [loading, setLoading] = useState(false);
  const { login, ssoLogin, ssoCallback, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // 初始化时设置多语言
  useEffect(() => {
    const { lang } = locale.getLocale();
    if (lang) {
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage("th-TH");
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Handle SSO callback - check for authorization code in URL
  useEffect(() => {
    const handleSSOCallbackFromUrl = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (code) {
        setLoading(true);
        try {
          // Clean the URL to remove code parameter
          window.history.replaceState({}, document.title, '/login');

          const success = await ssoCallback(code);
          if (success) {
            message.success(t("login.loginSuccess"));
            navigate("/dashboard");
          } else {
            message.error(t("login.loginFailed"));
          }
        } catch (error) {
          console.error("SSO callback error:", error);
          message.error(t("login.loginFailed"));
        } finally {
          setLoading(false);
        }
      }
    };

    handleSSOCallbackFromUrl();
  }, []);

  // 获取当前时间
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  // 处理密码登录
  const handlePasswordLogin = async (username, password) => {
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result === true || result?.success) {
        message.success(t("login.loginSuccess"));
        handleRedirect();
      } else if (result === false) {
        // 强制改密等场景，AuthContext 内部已弹窗处理，无需再提示
      } else if (result?.errorCode === "SC-A4210") {
        message.error(t("login.invalidAccountOrPassword"));
      } else {
        message.error(result?.errorMessage || t("login.loginFailed"));
      }
    } catch (error) {
      console.error(t("login.loginError"), error);
      message.error(t("login.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  // 处理 SSO 登录
  const handleSsoLogin = async () => {
    setLoading(true);
    try {
      const success = await ssoLogin();
      if (success) {
        message.success(t("login.loginSuccess"));
        handleRedirect();
      } else {
        message.error(t("login.loginFailed"));
      }
    } catch (error) {
      console.error("SSO login error:", error);
      message.error(t("login.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  // 处理重定向
  const handleRedirect = () => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirectUrl");
    if (redirect) {
      navigate(decodeURIComponent(redirect));
    } else {
      navigate("/dashboard");
    }
  };

  // 切换语言
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div className="login-container">
      {/* 左侧背景区域 */}
      <div className="login-left">
        <div className="login-left-content">
          <div className="login-logo">
            <span className="logo-text">LOTUS's</span>
          </div>
        </div>
      </div>

      {/* 右侧登录区域 */}
      <div className="login-right">
        {/* 顶部信息栏 */}
        <div className="login-header">
          <div className="login-brand">
            <span className="brand-text">LOTUS's</span>
          </div>
          <div className="login-time">
            <span className="time-text">{formatTime(currentTime)}</span>
            <span className="date-text">{formatDate(currentTime)}</span>
          </div>
        </div>

        {/* 登录表单区域 */}
        <div className="login-form-wrapper">
          <h1 className="login-title">{t("login.title")}</h1>

          {loginMode === "sso" ? (
            // SSO 登录模式
            <div className="sso-login-container">
              <Button
                type="primary"
                size="large"
                icon={<WindowsOutlined />}
                loading={loading}
                onClick={handleSsoLogin}
                className="sso-button"
              >
                {t("login.ssoButton")}
              </Button>

              <div className="login-divider">
                <span>{t("login.or")}</span>
              </div>

              <Button
                type="link"
                onClick={() => setLoginMode("password")}
                className="switch-mode-link"
              >
                {t("login.switchToPassword")}
              </Button>
            </div>
          ) : (
            // 密码登录模式
            <div className="password-login-container">
              <PasswordLoginForm onLogin={handlePasswordLogin} loading={loading} />

              <div className="login-divider">
                <span>{t("login.or")}</span>
              </div>

              <Button
                type="link"
                onClick={() => setLoginMode("sso")}
                className="switch-mode-link"
              >
                {t("login.switchToSso")}
              </Button>
            </div>
          )}
        </div>

        {/* 底部语言切换 */}
        <div className="login-footer">
          <Space size="middle">
            <Button
              type={i18n.language === "en" || i18n.language === "en-US" ? "primary" : "text"}
              size="small"
              onClick={() => handleLanguageChange("en")}
            >
              EN
            </Button>
            <Button
              type={i18n.language === "th" || i18n.language === "th-TH" ? "primary" : "text"}
              size="small"
              onClick={() => handleLanguageChange("th")}
            >
              TH
            </Button>
          </Space>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
